import React, { useState, useEffect } from 'react';
import { CreditCard, Webhook, CheckCircle2, AlertTriangle, Clock } from 'lucide-react';
import { adminAPI } from '../../api/api';

const MODE_BADGE = {
  live: { label: 'LIVE', cls: 'bg-green-100 text-green-700 border-green-300' },
  test: { label: 'TEST MODE', cls: 'bg-amber-100 text-amber-700 border-amber-300' },
  not_configured: { label: 'NOT CONFIGURED', cls: 'bg-red-100 text-red-700 border-red-300' },
};

const PayoutFeeEditor = ({ current, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(current);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await adminAPI.setPayoutFee(Number(value));
      onSaved(Number(value));
      setEditing(false);
    } catch { /* keep editing */ } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center justify-between text-sm bg-gray-50 rounded-lg px-3 py-2 mb-4" data-testid="payout-fee-row">
      <span className="text-gray-700 font-medium">Payout platform fee</span>
      {editing ? (
        <span className="flex items-center gap-2">
          <input
            type="number" min="0" max="15" step="0.5" value={value}
            onChange={(e) => setValue(e.target.value)}
            className="w-16 h-7 px-2 text-center border border-gray-300 rounded-md text-sm font-bold"
            data-testid="payout-fee-input"
          />
          <span className="text-gray-400">%</span>
          <button onClick={save} disabled={saving} className="text-xs font-bold text-white bg-[#2B4C6F] px-2 py-1 rounded-md" data-testid="payout-fee-save-btn">
            {saving ? '...' : 'Save'}
          </button>
          <button onClick={() => { setEditing(false); setValue(current); }} className="text-xs font-bold text-gray-400">Cancel</button>
        </span>
      ) : (
        <span className="flex items-center gap-2">
          <span className="font-bold text-gray-900" data-testid="payout-fee-value">{Number(current).toFixed(1)}%</span>
          <button onClick={() => { setValue(current); setEditing(true); }} className="text-xs font-bold text-[#2B4C6F] underline" data-testid="payout-fee-edit-btn">Edit</button>
        </span>
      )}
    </div>
  );
};

const STATUS_CLS = {
  paid: 'bg-green-100 text-green-700',
  pending: 'bg-amber-100 text-amber-700',
  failed: 'bg-red-100 text-red-700',
  expired: 'bg-gray-100 text-gray-500',
  refunded: 'bg-purple-100 text-purple-700',
};

const timeAgo = (iso) => {
  if (!iso) return '—';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (isNaN(s)) return '—';
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const shortPath = (url) => {
  try { return new URL(url).pathname; } catch { return url || '—'; }
};

export default function PaymentsHealthCard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    adminAPI.getPaymentsHealth()
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, []);

  if (error) return null;
  if (!data) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6" data-testid="payments-health-card">
        <p className="text-sm text-gray-400">Loading payments health…</p>
      </div>
    );
  }

  const mode = MODE_BADGE[data.stripe_mode] || MODE_BADGE.not_configured;
  const t30 = data.transactions_30d || {};

  return (
    <div className="bg-white rounded-xl shadow-md p-6" data-testid="payments-health-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-gray-900 flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-[#2B4C6F]" /> Payments Health
        </h3>
        <div className="flex items-center gap-2">
          {data.connect_enabled === false && (
            <span className="text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full border bg-red-100 text-red-700 border-red-300" data-testid="connect-status-badge">
              Connect: Not Enabled
            </span>
          )}
          {data.connect_enabled === true && (
            <span className="text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full border bg-green-100 text-green-700 border-green-300" data-testid="connect-status-badge">
              Connect: Enabled
            </span>
          )}
          <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full border ${mode.cls}`} data-testid="stripe-mode-badge">
            Stripe: {mode.label}
          </span>
        </div>
      </div>

      {data.connect_enabled === false && (
        <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4" data-testid="connect-disabled-warning">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            Worker/contractor bank payouts are blocked — Stripe Connect isn't activated on your account.
            Enable it once at{' '}
            <a href="https://dashboard.stripe.com/connect" target="_blank" rel="noreferrer" className="font-bold underline">
              dashboard.stripe.com/connect
            </a>{' '}
            (choose "Platform or marketplace", complete the profile). No code changes needed after that.
          </span>
        </div>
      )}

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-gray-50 rounded-lg p-3 text-center" data-testid="ph-paid-volume">
          <p className="text-xl font-bold text-gray-900">${(t30.paid_volume || 0).toLocaleString()}</p>
          <p className="text-[11px] text-gray-500">Paid volume (30d)</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center" data-testid="ph-paid-count">
          <p className="text-xl font-bold text-green-600">{t30.paid_count || 0}</p>
          <p className="text-[11px] text-gray-500">Paid (30d)</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center" data-testid="ph-fees-collected">
          <p className="text-xl font-bold text-[#2B4C6F]">${(data.fees_collected_30d || 0).toLocaleString()}</p>
          <p className="text-[11px] text-gray-500">Fees collected (30d)</p>
        </div>
      </div>

      <PayoutFeeEditor current={data.payout_fee_percentage || 0} onSaved={(pct) => setData((d) => ({ ...d, payout_fee_percentage: pct }))} />

      <div className="mb-4" data-testid="ph-webhooks">
        <p className="text-xs font-bold uppercase text-gray-500 mb-2 flex items-center gap-1">
          <Webhook className="h-3.5 w-3.5" /> Webhook Endpoints
        </p>
        {data.webhooks.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
            <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
            Not registered yet — auto-registers on first production request with a live key.
          </div>
        ) : (
          <div className="space-y-1.5">
            {data.webhooks.map((w) => (
              <div key={w.endpoint_url} className="flex items-center justify-between text-sm bg-gray-50 rounded-lg px-3 py-2">
                <span className="flex items-center gap-2 text-gray-700 font-mono text-xs truncate">
                  <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                  {shortPath(w.endpoint_url)}
                </span>
                <span className="text-[11px] text-gray-400 shrink-0 ml-2">
                  {w.events_count} events · {w.last_action} {timeAgo(w.last_checked_at)}
                </span>
              </div>
            ))}
          </div>
        )}
        <p className="text-[11px] text-gray-400 mt-1.5 flex items-center gap-1">
          <Clock className="h-3 w-3" />
          Last webhook received: {data.last_webhook_event ? `${data.last_webhook_event.event_type} · ${timeAgo(data.last_webhook_event.received_at)}` : 'none yet'}
        </p>
      </div>

      <div data-testid="ph-recent-transactions">
        <p className="text-xs font-bold uppercase text-gray-500 mb-2">Recent Transactions</p>
        {data.recent_transactions.length === 0 ? (
          <p className="text-sm text-gray-400">No transactions yet.</p>
        ) : (
          <div className="space-y-1.5">
            {data.recent_transactions.map((t, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="text-gray-700 truncate">{t.description}</span>
                <span className="flex items-center gap-2 shrink-0 ml-2">
                  <span className="font-bold text-gray-900">${Number(t.amount || 0).toLocaleString()}</span>
                  <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${STATUS_CLS[t.payment_status] || 'bg-gray-100 text-gray-500'}`}>
                    {t.payment_status}
                  </span>
                  <span className="text-[11px] text-gray-400">{timeAgo(t.created_at)}</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
